import { isPaidStatus, keptTotal, invoiceDisplayStatus } from '../utils/invoiceOverdue';   // Block H-5: refunds
import { useState, useMemo, useEffect } from "react";
import { useInvoices } from "../contexts/InvoicesContext";
import { useExpenses } from "../contexts/ExpensesContext";
import {
  loadProjects,
  calcInvoiceTotal,
  getDateRange,
  buildTrendData,
  groupByProject,
  groupByCategory,
  buildAnnualSummary,
} from "../services/financialReportService";
import { isInvoiceOverdue, isInvoicePayable, localToday } from "../utils/invoiceOverdue";
import { useCurrentUser } from "../contexts/CurrentUserContext";

// Decision 71: draft and cancelled invoices are not income anyone expects; they are left out of
// every total. Decision 72: no conversion. Sums are in the base currency; other currencies are
// totalled on their own and shown beside it ("£3,465 plus €1,200"), never added in.
const NOT_INCOME = new Set(['draft', 'cancelled']);

export function useFinancials() {
  // ── Date range state ──────────────────────────────────────────────────────
  const [preset, setPreset] = useState("this-month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const { start, end } = useMemo(() => {
    if (preset === "custom") {
      const s = customStart || localToday();
      const e = customEnd || localToday();
      return { start: s, end: e };
    }
    return getDateRange(preset) || { start: "", end: "" };
  }, [preset, customStart, customEnd]);

  // ── Raw data from contexts ────────────────────────────────────────────────
  // loadFailed / loaded: InvoicesContext tells "no invoices" from "could not load them", so the
  // page never shows a zero or an all-clear for a failed load. ExpensesContext has no such flag.
  const { invoices: allInvoices, loadFailed: invoicesFailed = false, loaded: invoicesLoaded = true, refresh: refreshInvoices } = useInvoices();
  const { expenses: allExpenses, refresh: refreshExpenses } = useExpenses();
  const [allProjects, setAllProjects] = useState([]);

  useEffect(() => {
    let alive = true;
    loadProjects()
      .then((projects) => {
        if (alive) setAllProjects(Array.isArray(projects) ? projects : []);
      })
      .catch(() => {
        if (alive) setAllProjects([]);
      });

    return () => {
      alive = false;
    };
  }, []);

  const { currentUser } = useCurrentUser();
  const baseCurrency = String(currentUser?.baseCurrency || currentUser?.currencyDisplay || 'GBP').toUpperCase();
  const curOf = (i) => String(i.currency || baseCurrency).toUpperCase();

  // ── Period-filtered data ──────────────────────────────────────────────────
  // Everything in the period that is real income (not draft, not cancelled), in any currency.
  const periodLive = useMemo(
    () => allInvoices.filter(i => i.issueDate >= start && i.issueDate <= end && !NOT_INCOME.has(i.status)),
    [allInvoices, start, end],
  );
  // The base-currency ones: every sum, chart and project figure uses only these.
  const invoices = useMemo(() => periodLive.filter(i => curOf(i) === baseCurrency), [periodLive, baseCurrency]); // eslint-disable-line react-hooks/exhaustive-deps

  const expenses = useMemo(
    () => allExpenses.filter(e => e.date >= start && e.date <= end),
    [allExpenses, start, end],
  );

  // ── Key metrics ───────────────────────────────────────────────────────────
  const today = localToday();

  const metrics = useMemo(() => {
    const sum = list => list.reduce((s, i) => s + calcInvoiceTotal(i.lineItems), 0);
    // Decision 44: overdue = still payable ('sent' or 'overdue') AND past its due date.
    const overdue = invoices.filter(i => isInvoiceOverdue(i, today));

    const totalInvoiced = sum(invoices);
    // Block H-5: received is what was KEPT (paid less refunds); pending is a sent invoice not yet due
    // (it matched status 'pending', which nothing writes, so it was always 0).
    const totalReceived = invoices.reduce((s, i) => s + keptTotal(i, calcInvoiceTotal(i.lineItems)), 0);
    const totalPending = sum(invoices.filter(i => invoiceDisplayStatus(i) === "pending"));
    const stillOwed = sum(invoices.filter(i => ["pending", "overdue"].includes(invoiceDisplayStatus(i))));
    const totalOverdue = sum(overdue);
    const totalExpenses = expenses.reduce((s, e) => s + Number(e.amount), 0);
    const netProfit = totalReceived - totalExpenses;

    return {
      totalInvoiced,
      totalReceived,
      // Everything invoiced in the period that has not been paid yet.
      // (It was invoiced less received, which counted a refunded invoice as still owed.)
      totalOutstanding: stillOwed,
      totalPending,
      totalOverdue,
      totalExpenses,
      netProfit,
      invoiceCount: invoices.length,
      paidCount: invoices.filter(i => isPaidStatus(i.status)).length,
      pendingCount: invoices.filter(i => invoiceDisplayStatus(i) === "pending").length,
      overdueCount: overdue.length,
      expenseCount: expenses.length,
      currency: baseCurrency,
      // The other currencies, each on its own (decision 72).
      others: (() => {
        const o = { invoiced: {}, received: {}, outstanding: {}, overdue: {} };
        for (const i of periodLive) {
          const c = curOf(i);
          if (c === baseCurrency) continue;
          const t = calcInvoiceTotal(i.lineItems);
          o.invoiced[c] = (o.invoiced[c] || 0) + t;
          if (isPaidStatus(i.status)) o.received[c] = (o.received[c] || 0) + keptTotal(i, t);
          else if (['pending', 'overdue'].includes(invoiceDisplayStatus(i))) o.outstanding[c] = (o.outstanding[c] || 0) + t;
          if (isInvoiceOverdue(i, today)) o.overdue[c] = (o.overdue[c] || 0) + t;
        }
        return o;
      })(),
    };
  }, [invoices, expenses, today, periodLive, baseCurrency]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Revenue trend (line chart series) ────────────────────────────────────
  const trendData = useMemo(
    () => (start && end ? buildTrendData(invoices, expenses, start, end) : []),
    [invoices, expenses, start, end],
  );

  // ── Income breakdown by project (top 8 + "Other") ─────────────────────────
  const byProject = useMemo(() => {
    const groups = groupByProject(invoices, allProjects, expenses);
    if (groups.length <= 8) return groups;
    const top = groups.slice(0, 8);
    const other = groups.slice(8).reduce(
      (acc, g) => ({
        ...acc,
        invoiced: acc.invoiced + g.invoiced,
        received: acc.received + g.received,
        expenses: acc.expenses + g.expenses,
        net: acc.net + g.net,
      }),
      { id: null, name: "Other", tag: "Other", invoiced: 0, received: 0, expenses: 0, net: 0 },
    );
    return [...top, other];
  }, [invoices, expenses, allProjects]);

  // ── Expense breakdown by category (doughnut) ──────────────────────────────
  const byCategory = useMemo(() => groupByCategory(expenses), [expenses]);

  // ── Annual summary (always full UK tax year — ignores date range) ─────────
  const annualSummary = useMemo(
    () => buildAnnualSummary(allInvoices.filter(i => !NOT_INCOME.has(i.status) && curOf(i) === baseCurrency), allExpenses),
    [allInvoices, allExpenses, baseCurrency], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ── Outstanding actions (not period-filtered — use ALL invoices/expenses) ─
  const in14Days = localToday(new Date(Date.now() + 14 * 86_400_000));

  const overdueInvoices = useMemo(
    () => allInvoices
      .filter(i => isInvoiceOverdue(i, today))
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate)),
    [allInvoices, today],
  );

  // Payable only (decision 44's statuses): a draft or a cancelled invoice is not a payment
  // anyone is expecting.
  const upcomingInvoices = useMemo(
    () => allInvoices
      .filter(i => isInvoicePayable(i) && i.dueDate >= today && i.dueDate <= in14Days)
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate)),
    [allInvoices, today, in14Days], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const unbilledExpenses = useMemo(
    () => allExpenses.filter(e => e.is_billable && !e.is_billed),
    [allExpenses],
  );

  return {
    // date range
    preset, setPreset,
    customStart, setCustomStart,
    customEnd, setCustomEnd,
    start, end,
    // data
    metrics,
    trendData,
    byProject,
    byCategory,
    annualSummary,
    overdueInvoices,
    upcomingInvoices,
    unbilledExpenses,
    allProjects,
    // raw for export: every invoice in the period, in its own currency, as before
    exportInvoices: allInvoices.filter(i => i.issueDate >= start && i.issueDate <= end),
    invoices,
    expenses,
    // load state
    invoicesLoaded,
    invoicesFailed,
    retry: () => { refreshInvoices?.(); refreshExpenses?.(); },
  };
}
